import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DateRange } from 'react-day-picker';

import {
  useCreateSavedSearchMutation,
  useDeleteSavedSearchMutation,
  useSavedSearches,
  useUpdateSavedSearchMutation,
} from '@/hooks/useSavedSearches';
import type {
  CreateSavedSearchPayload,
  SavedSearch,
  UpdateSavedSearchPayload,
} from '@/lib/api/savedSearches';
import { router } from '@/router';
import { ArticleSource } from '@/schema';

export type { SavedSearch } from '@/lib/api/savedSearches';

interface SavedSearchActions {
  create: (payload: CreateSavedSearchPayload) => Promise<SavedSearch>;
  update: (payload: UpdateSavedSearchPayload) => Promise<SavedSearch>;
  remove: (id: number) => Promise<void>;
}

interface FilterContextProps {
  selectedSymbols: string[];
  setSelectedSymbols: (symbols: string[]) => void;
  selectedSources: ArticleSource[];
  setSelectedSources: (sources: ArticleSource[]) => void;
  dateRange: DateRange | undefined;
  setDateRange: (dateRange: DateRange | undefined) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  hasSymbolOnly: boolean;
  setHasSymbolOnly: (hasSymbol: boolean) => void;
  clearAllFilters: () => void;
  savedSearches: SavedSearch[];
  savedSearchActions: SavedSearchActions;
  activeSavedSearchId: number | null;
  applySavedSearch: (id: number) => void;
  clearActiveSavedSearch: () => void;
}

const FilterContext = createContext<FilterContextProps | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<ArticleSource[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hasSymbolOnly, setHasSymbolOnly] = useState<boolean>(true);
  const [activeSavedSearchId, setActiveSavedSearchId] = useState<number | null>(
    null
  );

  const savedSearchesQuery = useSavedSearches();

  const savedSearches = useMemo(
    () => savedSearchesQuery.data ?? [],
    [savedSearchesQuery.data]
  );

  useEffect(() => {
    if (savedSearchesQuery.error) {
      console.warn('Failed to load saved searches', savedSearchesQuery.error);
    }
  }, [savedSearchesQuery.error]);

  const createSavedSearchMutation = useCreateSavedSearchMutation({
    onSuccess: (newSearch) => {
      setActiveSavedSearchId(newSearch.id);
    },
  });
  const updateSavedSearchMutation = useUpdateSavedSearchMutation();
  const deleteSavedSearchMutation = useDeleteSavedSearchMutation({
    onSuccess: (_data, id) => {
      setActiveSavedSearchId((prev) => (prev === id ? null : prev));
    },
  });

  const savedSearchActions = useMemo<SavedSearchActions>(
    () => ({
      create: (payload: CreateSavedSearchPayload) =>
        createSavedSearchMutation.mutateAsync(payload),
      update: (payload: UpdateSavedSearchPayload) =>
        updateSavedSearchMutation.mutateAsync(payload),
      remove: (id: number) => deleteSavedSearchMutation.mutateAsync(id),
    }),
    [
      createSavedSearchMutation,
      updateSavedSearchMutation,
      deleteSavedSearchMutation,
    ]
  );

  const applySavedSearch = useCallback(
    (id: number) => {
      const saved = savedSearches.find((search) => search.id === id);
      if (!saved) {
        return;
      }

      setSelectedSymbols([...saved.symbols]);
      setSelectedSources([...saved.sources]);
      setHasSymbolOnly(saved.hasSymbolOnly);
      setActiveSavedSearchId(id);
    },
    [savedSearches]
  );

  const clearActiveSavedSearch = useCallback(() => {
    setActiveSavedSearchId(null);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedSymbols([]);
    setDateRange(undefined);
    setSelectedSources([]);
    setSearchQuery('');
    setHasSymbolOnly(true);
    clearActiveSavedSearch();
  }, [clearActiveSavedSearch]);

  const haveSameElements = useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false;
    }

    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((value, index) => value === sortedB[index]);
  }, []);

  useEffect(() => {
    if (activeSavedSearchId === null) {
      return;
    }

    const activeSearch = savedSearches.find(
      (search) => search.id === activeSavedSearchId
    );

    if (!activeSearch) {
      setActiveSavedSearchId(null);
      return;
    }

    const symbolsMatch = haveSameElements(
      activeSearch.symbols,
      selectedSymbols
    );
    const sourcesMatch = haveSameElements(
      activeSearch.sources,
      selectedSources
    );
    const hasSymbolMatch =
      Boolean(activeSearch.hasSymbolOnly) === Boolean(hasSymbolOnly);

    if (!symbolsMatch || !sourcesMatch || !hasSymbolMatch) {
      setActiveSavedSearchId(null);
    }
  }, [
    activeSavedSearchId,
    savedSearches,
    selectedSymbols,
    selectedSources,
    hasSymbolOnly,
    haveSameElements,
  ]);

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', (event) => {
      if (event.toLocation.pathname !== event.fromLocation?.pathname) {
        clearAllFilters();
      }
    });

    return () => unsubscribe();
  }, [clearAllFilters]);

  const contextValue = useMemo(
    () => ({
      selectedSymbols,
      setSelectedSymbols,
      selectedSources,
      setSelectedSources,
      dateRange,
      setDateRange,
      searchQuery,
      setSearchQuery,
      hasSymbolOnly,
      setHasSymbolOnly,
      clearAllFilters,
      savedSearches,
      savedSearchActions,
      activeSavedSearchId,
      applySavedSearch,
      clearActiveSavedSearch,
    }),
    [
      selectedSymbols,
      selectedSources,
      dateRange,
      searchQuery,
      hasSymbolOnly,
      setSelectedSymbols,
      setDateRange,
      setSelectedSources,
      setSearchQuery,
      setHasSymbolOnly,
      clearAllFilters,
      savedSearches,
      savedSearchActions,
      activeSavedSearchId,
      applySavedSearch,
      clearActiveSavedSearch,
    ]
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
